import { PLATFORMS, PLATFORM_DETAIL } from '@/data/siteData'
import styles from '@/pages/PlatformPage.module.css'

/**
 * Renders the deep-dive informational sections shared by every platform page:
 * overview + stat strip, pillars, journey timeline, "who it's for", and FAQ.
 * All copy lives in PLATFORM_DETAIL (siteData.js); accent colours come from PLATFORMS.
 */
export default function PlatformDetail({ id }) {
  const platform = PLATFORMS.find(p => p.id === id)
  const detail = PLATFORM_DETAIL[id]
  if (!platform || !detail) return null

  const { overview, stats, pillars, journey, audience, faqs } = detail

  return (
    <>
      {/* ── OVERVIEW + STAT STRIP ── */}
      <section className={styles.overviewSection} aria-label={`${platform.name} overview`}>
        <div className={styles.overviewInner}>
          <p className="section-label">In a Nutshell</p>
          <p className={styles.overviewText}>{overview}</p>
        </div>
        <div className={styles.statStrip}>
          {stats.map(({ val, label, note }) => (
            <div key={label} className={styles.statBig} style={{ borderTop: `2px solid ${platform.color}` }}>
              <p className={styles.statBigVal} style={{ color: platform.color }}>{val}</p>
              <p className={styles.statBigLabel}>{label}</p>
              <p className={styles.statBigNote}>{note}</p>
            </div>
          ))}
        </div>
      </section>

      {/* ── PILLARS ── */}
      <section className={styles.pillarsSection} aria-label={`What makes ${platform.name} different`}>
        <div className={styles.pillarsInner}>
          <p className="section-label">What You Get</p>
          <h2 className="section-title">Everything {platform.name} brings to the table</h2>
          <div className={styles.pillarGrid}>
            {pillars.map(({ icon, title, desc }) => (
              <div key={title} className={styles.pillarCard}>
                <div className={styles.pillarIcon} style={{ background: platform.colorBg }}>{icon}</div>
                <div>
                  <h3 className={styles.featTitle}>{title}</h3>
                  <p className={styles.featDesc}>{desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── JOURNEY TIMELINE ── */}
      <section className={styles.timelineSection} aria-label={`How ${platform.name} works`}>
        <div className={styles.timelineInner}>
          <p className="section-label">Step by Step</p>
          <h2 className="section-title">Your path on {platform.name}</h2>
          <div className={styles.timeline}>
            {journey.map((step, i) => (
              <div key={step.title} className={styles.tlStep}>
                <div
                  className={styles.tlNum}
                  style={{ background: platform.colorBg, color: platform.color, border: `1px solid ${platform.colorBorder}` }}
                >
                  {i + 1}
                </div>
                <div>
                  <p className={styles.tlTitle}>{step.title}</p>
                  <p className={styles.tlDesc}>{step.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── AUDIENCE ── */}
      <section className={styles.audienceSection} aria-label={`Who ${platform.name} is for`}>
        <div className={styles.audienceInner}>
          <p className="section-label">Who It's For</p>
          <h2 className="section-title">Built for every kind of learner</h2>
          <div className={styles.audienceGrid}>
            {audience.map(({ icon, title, desc }) => (
              <div key={title} className={styles.audienceCard}>
                <div className={styles.audienceIcon}>{icon}</div>
                <h3 className={styles.featTitle}>{title}</h3>
                <p className={styles.featDesc}>{desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── FAQ ── */}
      <section className={styles.faqSection} aria-label={`${platform.name} frequently asked questions`}>
        <div className={styles.faqInner}>
          <p className="section-label">Questions</p>
          <h2 className="section-title">Frequently asked</h2>
          <div className={styles.faqList}>
            {faqs.map(({ q, a }) => (
              <details key={q} className={styles.faqItem}>
                <summary className={styles.faqQ}>{q}</summary>
                <p className={styles.faqA}>{a}</p>
              </details>
            ))}
          </div>
        </div>
      </section>
    </>
  )
}
